import { IconChevronLeft } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ROOT_SLUG } from "../../../const/enviroment";
import { EMPTY_CATEGORY } from "../../../const/typeDefault/Category.ts";
import {
  useCategory,
  useCategoryParent,
} from "../../../fetch/fetch.category.ts";
import { useActiveParams } from "../../../hooks/useActiveParams.ts";
import { LOADING_CIRCLE } from "../../core/loading/LoadingCircle.tsx";
import ReactiveToggle from "../../core/ReactiveToggle.tsx";
import ItemCount from "../../item/core/ItemCount.tsx";
import CategoryImage from "../core/CategoryImage";
import CategoryTitle from "../core/CategoryTitle.tsx";
import ParentCategoryList from "../list/ParentCategoryList.tsx";
import SubcategoryList from "../list/SubcategoryList.tsx";

function BackCategory({
  slug,
  routeTo,
  routeEval,
}: {
  slug: string;
  routeTo: string;
  routeEval: (categoryId: string) => string;
}) {
  const isRoot = slug === ROOT_SLUG;
  const navigate = useNavigate();
  const { categorySlug } = useActiveParams();
  const { category } = useCategory(slug, EMPTY_CATEGORY);
  const selectCategory = useCallback(() => {
    navigate(routeTo);
  }, [navigate, routeTo]);
  if (categorySlug !== slug) return null;

  return (
    <div className="flex flex-col gap-1 ">
      <div className="flex items-center">
        <ReactiveToggle transitional={selectCategory}>
          {(handleClick, isPending) => (
            <button
              type="button"
              className={`flex items-center rounded-full group shadow-lg p-0.5 hover:cursor-pointer glass-blurry hover:text-error active:text-error w-full  ${isRoot ? "opacity-0 h-0" : "opacity-100"} transition-all`}
              onClick={handleClick}
            >
              <div className="w-5">
                {isPending ? LOADING_CIRCLE : <IconChevronLeft />}
              </div>

              <div className="stack stack-start w-full">
                <div
                  className={`flex items-center text-xl group-active:btn-active group-hover:btn-active btn gap-2 h-full p-1 rounded-full grow`}
                >
                  <figure className="aspect-square h-12">
                    <CategoryImage
                      size={12}
                      slug={slug}
                      sizes="128px"
                      className="object-cover w-full h-full object-center rounded-full"
                    />
                  </figure>
                  <div className="flex items-center justify-between w-full mr-4">
                    <CategoryTitle slug={slug} />
                    <div className="badge">
                      <ItemCount categorySlug={slug} />
                    </div>
                  </div>
                </div>

                <ParentCategoryList slug={categorySlug} showRoot={false}>
                  {() => (
                    <div className="badge badge-neutral bg-base-300/30 badge-outline h-full rounded-full"></div>
                  )}
                </ParentCategoryList>
              </div>
            </button>
          )}
        </ReactiveToggle>
      </div>
      {category.subCategories.length > 0 && (
        <div className="mx-4">
          <SubcategoryList slug={categorySlug} routeEval={routeEval} />
        </div>
      )}
    </div>
  );
}

const CategoryList = ({
  categorySlug,
  routeTo,
}: {
  categorySlug: string;
  routeTo: (id: string) => string;
}) => {
  const { category } = useCategoryParent(categorySlug, EMPTY_CATEGORY);

  return categorySlug === ROOT_SLUG ? (
    <div className="px-4">
      <SubcategoryList slug={categorySlug} routeEval={routeTo} />
    </div>
  ) : (
    category.subCategories.map((id) => (
      <BackCategory
        key={id}
        slug={id}
        routeTo={routeTo(category.slug)}
        routeEval={routeTo}
      />
    ))
  );
};

const CategorySelectorContainer = ({
  children,
  routeTo,
}: {
  children?: ReactNode;
  routeTo: (routeTo?: string) => string;
}) => {
  const { categorySlug } = useActiveParams();

  return (
    <div className="flex flex-col gap-1 rounded-box w-full transition-all">
      {children}

      <CategoryList categorySlug={categorySlug} routeTo={routeTo} />
    </div>
  );
};
export default CategorySelectorContainer;
